
import { AiCopywriterConfig } from "@/common/entity/AppConfigs";
import { Response } from "@/common/entity/Response";
import { COUNT_NO_LIMIT, getUserFrom, User } from "@/common/entity/User";
import { RouterTools } from "@/common/RouterTools";
import TextareaBox from "@/components/textarea-box/index.vue";
import TopTips from "@/components/top-tips/index.vue";
import { TopTipsTip, TopTipsTips } from "@/components/top-tips/TopTips";
import { AiCopywritingApis } from "@/request/ai-copywriting";
import {
  AiCopywritingInfoOptions,
  AiCopywritingInfos,
  RootGetterNames,
  RootMutationNames,
  RootState,
  RootStateNames,
} from "@/vuex-store";
import { Message, MessageBox } from "element-ui";
import { MessageBoxCloseAction } from "element-ui/types/message-box";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter, Mutation, State } from "vuex-class";
import { DEFAULT_COPYWRITING_LENGTH } from "./AiCopywritingPage";

@Component({
  components: { TopTips, TextareaBox },
})
export default class AiCopywriter extends Vue {
  /** 顶部提示 */
  tips: TopTipsTips = [
    "文案根据提示由AI自动生成，生成后扣除次数",
    "请确保您的提示和内容不能违反国家法律,违者追究法律责任",
    { text: "该功能为测试阶段，不保证永久可用，介意勿用", colorType: "error" },
    {
      text: "不保证文案每次成功，如果合成文案合成失败，不会扣您的次数",
      colorType: "error",
    },
  ];
  /** 文案描述输入框placeholder */
  descPlaceholder: string =
    "1、请简要描述您要生成的文案主题，\n2、例如：\n演讲稿：\n写一篇关于每个人如何永不放弃的演讲稿。将能够激发行动的词语放在一起，让人们感到有能力做一些超出他们能力的事情。你可以谈论任何话题，但目的是确保你所说的话能引起听众的共鸣，激励他们努力实现自己的目标并争取更好的可能性。";
  /** 支付界面路由地址 */
  payRoutePath: string = RouterTools.pagePaths.pay;

  /** store中的该界面内容状态值 */
  @State(RootStateNames.aiCopywritingInfos)
  readonly storeInfos!: AiCopywritingInfos;
  /** 修改store中的该界面内容状态值 */
  @Mutation(RootMutationNames.updateAiCopywritingInfos)
  readonly updateStoreInfos!: (infos: AiCopywritingInfoOptions) => void;

  /** 是否已经登录 */
  @Getter(RootGetterNames.isLogined)
  readonly isLogined!: boolean;

  /** 智能创作配置信息 */
  get configs(): AiCopywriterConfig | null {
    return (this.$store.state as RootState).appConfigs?.aiCopywriter || null;
  }

  /** AI功能次数剩余 */
  @State(RootStateNames.currUser)
  readonly currUser!: User | null;
  /** AI功能次数剩余显示文本 */
  get aiFuncCountText(): string {
    if (!this.currUser) return "未登录";
    return this.currUser.correctCount > 0
      ? `当前AI功能次数剩余：${this.currUser.correctCount}`
      : this.currUser.ai_copywriting_test_count > 0
      ? `当前试用次数剩余：${this.currUser.ai_copywriting_test_count}`
      : "当前没有AI功能次数，无法使用此功能";
  }

  /** 文案描述模板例子URL */
  get descTemplateUrl(): string | null {
    return this.configs?.descExampleUrl || null;
  }

  /** 文案描述getter */
  get copywritingDesc(): string {
    return this.storeInfos.desc;
  }
  /** 文案描述setter */
  set copywritingDesc(value: string) {
    this.updateStoreInfos({ desc: value });
  }
  /** 文案描述最大字数，负值表示不限制 */
  get maxDescLength(): number {
    return this.configs?.maxDescLength || -1;
  }
  /** 文案描述输入框下方的字数提示或错误信息提示 */
  get copywritingDescSizeTips(): TopTipsTip | null {
    if (this.maxDescLength < 0) return null;
    const sizeExceeded = this.copywritingDesc.length - this.maxDescLength;
    const warningSize = this.maxDescLength - 50;
    return {
      colorType:
        this.copywritingDesc.length > this.maxDescLength
          ? "error"
          : warningSize > 0 && this.copywritingDesc.length > warningSize
          ? "warning"
          : "info",
      text:
        `当前字数${this.copywritingDesc.length}，最大${this.maxDescLength}` +
        (sizeExceeded > 0
          ? `，已超出${sizeExceeded}`
          : `，剩余${-sizeExceeded}`),
    };
  }
  /** 点击清空文本按钮 */
  onClickClearDesc() {
    this.copywritingDesc = "";
  }

  /** 生成文案字数选项最大值 */
  get maxCopywritingLength(): number {
    return this.configs?.maxLength || Infinity;
  }
  /** 当前用户期待的文案字数getter */
  get copywritingLength(): number {
    return this.storeInfos.length;
  }
  /** 当前用户期待的文案字数setter */
  set copywritingLength(value: number) {
    this.updateStoreInfos({
      length: value || DEFAULT_COPYWRITING_LENGTH,
    });
  }
  /** 生成文案的字数输入错误信息 */
  copywritingLengthErr: string = "";
  /** 监听用户输入的文案字数，检查和显示字数错误信息 */
  @Watch("copywritingLength")
  onCopywritingLengthChange(length: number | undefined) {
    if (length === undefined) return;
    if (length > this.maxCopywritingLength)
      this.copywritingLengthErr = `生成的文案字数最大不会超过${this.maxCopywritingLength}`;
    else this.copywritingLengthErr = "";
  }
  /** 文案字数输入框失去焦点时，如果内容为空则把值设为{@link DEFAULT_COPYWRITING_LENGTH} */
  onCopywritingLengthBlur() {
    if (!this.copywritingLength)
      this.copywritingLength = DEFAULT_COPYWRITING_LENGTH;
  }

  /** 空文案风格label */
  emptyCopywritingStyleLabel: string = "不限定风格";
  /** 文案风格选项 */
  get copywritingStyles(): { value: string; label: string }[] {
    const styles = this.configs?.styles;
    if (!styles) return [];
    return [
      { label: this.emptyCopywritingStyleLabel, value: "" },
      ...styles.map((style: string) => {
        return { value: style, label: style };
      }),
    ];
  }
  /** 当前选择的文案风格getter */
  get currCopywritingStyle(): string {
    return this.storeInfos.style;
  }
  /** 当前选择的文案风格setter */
  set currCopywritingStyle(value: string) {
    this.updateStoreInfos({ style: value });
  }

  /** 是否正在生成文案 */
  generatingCopywriting: boolean = false;
  /** 生成文案错误信息 */
  generateCopywritingError: string | null = null;
  /** 生成的文案结果getter */
  get copywriting(): string {
    return this.storeInfos.result;
  }
  /** 生成的文案结果setter */
  set copywriting(value: string) {
    this.updateStoreInfos({ result: value });
  }

  /** 点击“生成文案”按钮时调用 */
  async onClickCreateCopywriting() {
    // 正在生成时直接return
    if (this.generatingCopywriting) return;

    // 检测是否已经登录
    if (!this.isLogined) {
      this.$store.commit(RootMutationNames.setShowLoginPanel, true);
      return;
    }

    // 检查输入的文案描述是否为空
    if (!this.copywritingDesc) {
      Message.error("文案描述不能为空");
      return;
    }

    // 检查文案描述字数错误信息
    if (this.copywritingDescSizeTips?.colorType === "error") {
      Message.error(this.copywritingDescSizeTips.text);
      return;
    }

    // 检查需要生成文案的文案字数
    if (this.copywritingLengthErr) {
      Message.error(this.copywritingLengthErr);
      return;
    }

    // 检查是否已经有生成结果
    if (this.copywriting) {
      await new Promise((resolve, reject) => {
        MessageBox.confirm(
          "生成结果后将会覆盖上一次生成的结果，是否“继续”",
          "是否继续？",
          {
            confirmButtonText: "继续",
            callback: (action: MessageBoxCloseAction) =>
              action === "confirm" ? resolve(undefined) : reject(),
          }
        );
      });
    }

    // 开始生成文案
    this.generateCopywritingError = null;
    this.generatingCopywriting = true;
    this.copywriting = "";
    try {
      const { userInfo, record } = await AiCopywritingApis.generate(
        this.copywritingDesc,
        this.copywritingLength || DEFAULT_COPYWRITING_LENGTH,
        this.currCopywritingStyle,
        false
      );
      this.$store.commit(RootMutationNames.setCurrUser, getUserFrom(userInfo));
      this.copywriting = record.result;
      Message.success("文案生成成功");
    } catch (e) {
      console.error(e);
      if (e instanceof Response) this.generateCopywritingError = e.userMsg;
    }
    this.generatingCopywriting = false;
  }
  /** 文案结果区域的提示 */
  get copywritingTips(): TopTipsTip {
    return this.generatingCopywriting
      ? { colorType: "info", text: "文案生成中，请稍后..." }
      : this.generateCopywritingError
      ? { colorType: "error", text: this.generateCopywritingError }
      : {
          colorType: "info",
          text: "输入文案描述，点击“生成文案”按钮即可生成您需要的文案，并显示在此",
        };
  }
  /** 点击复制文案按钮 */
  async onClickCopy() {
    if (!this.copywriting) {
      Message.error("文案内容为空");
      return;
    }
    await navigator.clipboard.writeText(this.copywriting);
    Message.success("复制成功");
  }
}
